import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as FacilityIcon } from '../../assets/icons/Device.svg';
import { ReactComponent as BackIcon } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/sidebar/sidebar-guest-icon.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableGuestCharging,
  RequestGuestChargingInterface,
} from '../../interface/partner-interfaces/guest-charging.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slices/NotificationSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  fetchGuestCharging,
  fetchListFilters,
  updateGuestFilters,
  setGuestChargingFilterData,
  setSortData,
  refreshGuestCharging,
} from '../../redux/slices/Partners/GuestChargingSlice';
import { RootState } from '../../redux/store';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import GuestChargingSidePanel from '../../shared-components/table-side-panel/GuestChargingSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import guestChargingApi from '../../utils/api/partner/guest-charging';

import AddGuestCharging from './components/AddGuestCharging';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Station management page
 */
function GuestCharging() {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('guestCharging');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isSidePanelOpen, setSidePanelOpen] = useState(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [selectedGuest, setSelectedGuest] = useState('');
  const [guestSidePanelPage, setGuestSidePanelPage] = useState<'view' | 'edit' | 'history'>('view');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const {
    guestCharging,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.partnerGuestCharging);
  const { currentFacilityTagsList } = useAppSelector(
    (state: RootState) => state.partnersFacilityTags,
  );

  /**
   * Handle the fetch data for Api call function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestGuestChargingInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchGuestCharging({
          ...data,
        }),
      );
    }
  };

  /**
   * Confirm the status change function
   * @param {any} id - The id
   * @param {any} checked - The checked
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    guestChargingApi
      .updateGuestStatus(id, { isActive: checked })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusUpdatedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page: DefaultApiLimit.page, limit });
                // dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            // dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {any} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableGuestCharging, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableDeviceWarningTitle'),
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableDeviceWarningMsg')}
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };
  /**
   * Renders the custom data for the availability column
   * @param {any} device - The device data
   * @returns {any} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableGuestCharging) => (
    <div
      className="flex justify-center"
      role="presentation"
      onClick={(even) => even.stopPropagation()}>
      <div key={device.id}>
        <SwitchButton
          checked={device.isActive}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * Renders the time
   * @param {any} device - The device
   * @returns {any} The result
   */
  const renderTime = (device: AvailableGuestCharging) => (
    <div className="charging-time-list">
      <div className="first-time">{device.time[0]}</div>
      <div className="count">{device.time.length - 1}</div>
    </div>
  );

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setGuestChargingFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    setModalBoxStatus('close');
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...value, ...queryParameters, sortField, sortBy });
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('consumerId'), accessorKey: 'userCustomId' },
    { label: t('name'), accessorKey: 'name' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('email'), accessorKey: 'email' },
    { label: t('assignedStation'), accessorKey: 'chargingStationNames' },
    { label: t('device'), accessorKey: 'chargingPointSerials' },
    { label: t('date'), accessorKey: 'fromDate' },
    { label: t('time'), accessorKey: 'timeSlots' },
    { label: t('duration'), accessorKey: 'duration' },
    { label: t('status'), accessorKey: 'isAvailable' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    guestChargingApi
      .fetchGuestChargingExport({ ...queryParameters, sortBy, sortField })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableGuestCharging, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'fromDate':
                return `${ConvertUTCtoDate(row.fromDate)} - ${ConvertUTCtoDate(row.toDate)}`;
              case 'timeSlots':
                return `${FormatUtcString(row?.timeSlots?.[0]?.startTime)} - ${FormatUtcString(row?.timeSlots?.[0]?.endTime)}`;
              case 'isAvailable':
                return row?.isActive ? t('active') : t('inactive');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'guest-charging-list.csv');
          } else {
            exportPDF(headers, rows, 'guest-charging-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  const column = [
    {
      label: t('consumerId'),
      accessorKey: 'userCustomId' as keyof AvailableGuestCharging,
      sortAllow: false,
    },
    {
      label: t('name'),
      accessorKey: 'name' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber' as keyof AvailableGuestCharging,
      sortAllow: false,
      /**
       * Device view
       * @param {any} station - The station
       */
      customData: (station: AvailableGuestCharging) => (
        <div className="table-row-each-data">
          {station.phoneNumber ? `+${station?.countryCode}  ${station?.phoneNumber}` : 'N/A'}
        </div>
      ),
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof AvailableGuestCharging,
      sortAllow: false,
      /**
       * Device view
       * @param {any} station - The station
       */
      customData: (station: AvailableGuestCharging) => (
        <div className="table-row-each-data">{station.email ? ` ${station?.email}` : 'N/A'}</div>
      ),
    },
    {
      label: t('assignedStation'),
      accessorKey: 'chargingStationNames' as keyof AvailableGuestCharging,
      sortAllow: false,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSerials' as keyof AvailableGuestCharging,
      sortAllow: false,
      /**
       * Device view
       * @param {any} station - The station
       */
      customData: (station: AvailableGuestCharging) => (
        <div className="table-row-each-data">{station?.chargingPoints?.join(', ')}</div>
      ),
    },
    {
      label: t('date'),
      accessorKey: 'fromDate' as keyof AvailableGuestCharging,
      sortAllow: true,
      /**
       * Date view
       * @param {any} station - The station
       */
      customData: (station: AvailableGuestCharging) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {ConvertUTCtoDate(station.fromDate)} - {ConvertUTCtoDate(station.toDate)}
        </div>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'timeSlots' as keyof AvailableGuestCharging,
      sortAllow: false,
      columnWidth: 160,
      customData: renderTime,
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof AvailableGuestCharging,
      sortAllow: true,
    },
    {
      label: t('status'),
      accessorKey: 'isAvailable' as keyof AvailableGuestCharging,
      customData: renderAvailability,
    },
  ];

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Handle the open side panel on row click function
   * @param {any} _ string
   * @param {any} row FacilityTags
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableGuestCharging) => {
    if (!row?._id) return;
    // Set or update the Id param
    searchParameters.set('guestId', row?._id);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
    setGuestSidePanelPage('view');
    dispatch(setPageTitle({ title: t('facility'), icon: <FacilityIcon />, count: undefined }));
  };

  /**
   * Add account function
   */
  const addGuestCharging = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('facilityId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  /**
   * Handle the auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshGuestCharging({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handle the guest delete success function
   */
  const handleGuestDeleteSuccess = () => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      limit,
      page,
      sortBy,
      sortField,
    });
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('guestId');
    setSearchParameters(parameters);
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit,
      page,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('guestId') || searchParameters.get('add');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('guests'),
          icon: <UserIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('guests'),
          icon: <UserIcon />,
          count: undefined,
        }),
      );
    }
  }, [searchParameters, totalResults, t]);

  useEffect(() => {
    const guestId = searchParameters.get('guestId');
    setSelectedGuest(guestId || '');
  }, [searchParameters]);

  return (
    <div className="guest-charging-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          loading={loading}
          limit={limit}
          header={column}
          data={guestCharging}
          page={page}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          exportDataPdfOrCSV={
            permissionCheck('mobilityGuestUserExport') ? exportDataPdfOrCSV : undefined
          }
          exportLoader={exportLoader}
          filters={filters}
          filtersToDispatch={updateGuestFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          version="v2"
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="guestId"
          rowClickSidePanel={
            <GuestChargingSidePanel
              selectedGuest={selectedGuest}
              onDeleteSuccess={handleGuestDeleteSuccess}
              currentPage={guestSidePanelPage}
              setCurrentPage={setGuestSidePanelPage}
            />
          }
          sidePanelTopLeftContent={
            guestSidePanelPage === 'history' ? (
              <BackIcon
                className="icon back"
                style={{
                  cursor: 'pointer',
                  transform: i18n.dir() === 'rtl' ? 'rotate(0deg)' : 'rotate(180deg)',
                }}
                onClick={() => setGuestSidePanelPage('view')}
              />
            ) : undefined
          }
          sidePanelEnabled={isSidePanelOpen}
          sidePanelManualContent={
            <AddGuestCharging
              closeSidePanel={() => {
                const parameters = new URLSearchParams(searchParameters);
                parameters.delete('guestId');
                setSearchParameters(parameters);

                setSidePanelOpen(false);
              }}
            />
          }
          onSidePanelClose={() => {
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('guestId');
            setSearchParameters(parameters);

            setSidePanelOpen(false);
          }}
          rightTopContent={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <MyButton
                  label={t('addGuest')}
                  buttonType="submit"
                  onClickFunc={addGuestCharging}
                  svgIcon={<PlusIcon />}
                  disableButton={!permissionCheck('mobilityGuestUserAdd')}
                />
              </div>
            </div>
          }
          refreshFunction={handleAutoRefresh}
          requiredRefreshToggle
          sizeChangeButtonRequired
        />
      </div>
    </div>
  );
}

export default GuestCharging;
